import { Component, HostListener, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BaseService } from '../../base.service';
import { UserAuthService } from '../../auth.service';
import { SPECTRUM_API_PATH, SPECTRUM_AUTH_API } from '../../tokens';
import { SpectrumModule, SpectrumModuleService } from '../module.service';
import { SpectrumSchoolChangeComponent } from '../school-change/school-change.component';

/**
 * Shared Spectrum header bar — same markup and stylesheet as the
 * Admissions CRM header (school card, Switch school, module switcher,
 * profile menu). The CRM keeps its own header because it adds
 * CRM-specific widgets (notification bell, academic-year picker).
 */
@Component({
    selector: 'spectrum-header-bar',
    imports: [CommonModule, FormsModule, SpectrumSchoolChangeComponent],
    templateUrl: './header-bar.component.html',
    styleUrl: './header-bar.component.scss'
})
export class SpectrumHeaderBarComponent implements OnInit {

    /** Label shown when the current origin doesn't match a module route. */
    @Input() moduleName = '';

    /** Kicker above the org name, e.g. "Current School" / "Current Group". */
    @Input() orgKicker = 'Current School';

    /** Overrides the sessionStorage school name (Group Admin shows its group name). */
    @Input() orgName = '';

    /** Hide the school switcher in apps that aren't school-scoped (Group Admin). */
    @Input() showSchoolSwitch = true;

    private auth = inject(UserAuthService);
    private base = inject(BaseService);
    private apiPath = inject(SPECTRUM_API_PATH);
    private authBase = inject(SPECTRUM_AUTH_API);
    private moduleService = inject(SpectrumModuleService);

    showSchoolChange = false;
    showModuleMenu = false;
    showProfileMenu = false;
    schoolLogoUrl = '';
    modules: SpectrumModule[] = [];
    moduleSearch = '';

    get schoolName(): string {
        return this.orgName || this.auth.getSchoolName() || 'School';
    }

    get employeeName(): string {
        return this.auth.getEmployeeName() || 'User';
    }

    get initials(): string {
        return this.employeeName
            .split(' ')
            .filter(Boolean)
            .map(part => part[0])
            .join('')
            .substring(0, 2)
            .toUpperCase() || 'U';
    }

    get currentModule(): SpectrumModule | null {
        return this.moduleService.currentModule(this.modules);
    }

    get sessionLabel(): string {
        return this.currentModule?.moduleName || this.moduleName || 'Spectrum';
    }

    get filteredModules(): SpectrumModule[] {
        const q = this.moduleSearch.trim().toLowerCase();
        if (!q) return this.modules;
        return this.modules.filter(module => module.moduleName.toLowerCase().includes(q));
    }

    ngOnInit(): void {
        this.loadSchoolLogo();
        this.moduleService.getModules().subscribe(modules => this.modules = modules);
    }

    loadSchoolLogo(): void {
        const params = new URLSearchParams({
            module: 'School',
            fileName: 'logo',
            contentType: 'image/jpeg'
        });
        this.base.get<{ URL?: string; url?: string }>(`${this.apiPath}/documents/get?${params.toString()}`)
            .subscribe({
                next: res => { this.schoolLogoUrl = res?.URL || res?.url || ''; },
                error: () => { this.schoolLogoUrl = ''; }
            });
    }

    handleLogoError(): void {
        this.schoolLogoUrl = '';
    }

    toggleModuleMenu(event: Event): void {
        event.stopPropagation();
        this.showModuleMenu = !this.showModuleMenu;
        if (this.showModuleMenu) {
            this.moduleSearch = '';
            this.showProfileMenu = false;
        }
    }

    toggleProfileMenu(event: Event): void {
        event.stopPropagation();
        this.showProfileMenu = !this.showProfileMenu;
        if (this.showProfileMenu) {
            this.showModuleMenu = false;
        }
    }

    @HostListener('document:click')
    closeMenus(): void {
        this.showModuleMenu = false;
        this.showProfileMenu = false;
    }

    @HostListener('document:keydown.escape')
    closeMenusOnEscape(): void {
        this.closeMenus();
        this.closeSchoolChange();
    }

    switchModule(module: SpectrumModule): void {
        if (this.moduleService.isCurrent(module, this.modules)) {
            this.showModuleMenu = false;
            return;
        }
        this.moduleService.switchModule(module);
    }

    openMyProfile(): void {
        this.moduleService.openMyProfile();
    }

    logout(): void {
        this.auth.logout();
        window.location.href = this.authBase;
    }

    openSchoolChange(): void { this.showSchoolChange = true; }
    closeSchoolChange(): void { this.showSchoolChange = false; }
}
